import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { usePrivacy } from '@/lib/privacy';
import { Button, Dot, cx } from '@/components/ui';
import { RecordModal } from '@/components/RecordModal';
import { VerifyEmailBanner } from '@/components/VerifyEmailBanner';
import { PrivacyPrompt, PrivacyToggle } from '@/components/PrivacyToggle';
import { ToastHost, useToast } from '@/components/ToastHost';
import { SearchIcon, TabIcon, type TabName } from '@/components/icons';
import logo from '@/assets/sterling_logo.png';
import s from './AppShell.module.css';

type NavigationItem = { to: string; label: string; icon: TabName; end?: boolean };

/** One catalogue for desktop navigation and the complete mobile bar. */
const NAV: NavigationItem[] = [
  { to: '/app', label: 'Overview', icon: 'overview', end: true },
  { to: '/app/accounts', label: 'Accounts', icon: 'accounts' },
  { to: '/app/transactions', label: 'Transactions', icon: 'transactions' },
  { to: '/app/budgets', label: 'Budgets', icon: 'budgets' },
  { to: '/app/analytics', label: 'Analytics', icon: 'analytics' },
  { to: '/app/advisor', label: 'Advisor', icon: 'advisor' },
  { to: '/app/investments', label: 'Investments', icon: 'investments' },
  { to: '/app/settings', label: 'Settings', icon: 'settings' },
];

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { masked } = usePrivacy();
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const bottomNavRef = useRef<HTMLElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });
  const [menuOpen, setMenuOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [search, setSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const navigation: NavigationItem[] =
    user?.role === 'ADMIN'
      ? [...NAV, { to: '/app/admin', label: 'Admin portal', icon: 'settings' }]
      : NAV;

  // Screens with a viewport-sized pane use the actual header height, including
  // the installed app's safe-area inset, rather than a desktop-sized constant.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--header-h');
    const measure = () =>
      root.style.setProperty('--header-h', `${header.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    measure();
    return () => {
      observer.disconnect();
      if (previous) root.style.setProperty('--header-h', previous);
      else root.style.removeProperty('--header-h');
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Reserve the bar's real height, including the home-indicator safe area.
  // Route changes and resize keep the active destination visible horizontally
  // without scrolling the page itself.
  useEffect(() => {
    const bar = bottomNavRef.current;
    if (!bar) return;
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--bottom-nav-h');
    const revealActive = () => {
      const active = bar.querySelector<HTMLElement>('[aria-current="page"]');
      if (active && bar.clientWidth) {
        const target = active.offsetLeft - (bar.clientWidth - active.offsetWidth) / 2;
        bar.scrollTo({ left: Math.max(0, target), behavior: 'instant' });
      }
    };
    const measure = () => {
      const bounds = bar.parentElement!.getBoundingClientRect();
      root.style.setProperty(
        '--bottom-nav-h',
        `${bounds.height ? window.innerHeight - bounds.top : 0}px`,
      );
      revealActive();
      updateScrollEdges();
    };
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      if (previous) root.style.setProperty('--bottom-nav-h', previous);
      else root.style.removeProperty('--bottom-nav-h');
    };
  }, [location.pathname, user?.role]);

  function updateScrollEdges() {
    const bar = bottomNavRef.current;
    if (!bar) return;
    const next = {
      left: bar.scrollLeft > 2,
      right: bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2,
    };
    setScrollEdges((previous) =>
      previous.left === next.left && previous.right === next.right ? previous : next,
    );
  }

  function navigationLinks(mobile: boolean) {
    return navigation.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          cx(
            mobile ? s.bottomLink : s.navLink,
            isActive && (mobile ? s.bottomLinkActive : s.navLinkActive),
          )
        }
      >
        {mobile ? (
          <TabIcon name={item.icon} className={s.navigationIcon} />
        ) : item.icon === 'advisor' ? (
          <Dot lime pulse />
        ) : null}
        {item.label}
      </NavLink>
    ));
  }

  // Close the account menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // The accent is a per-user preference; push it into the token so every
  // component picks it up without prop drilling.
  useEffect(() => {
    if (user?.accentColor) {
      document.documentElement.style.setProperty('--accent', user.accentColor);
    }
  }, [user?.accentColor]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate(`/app/transactions?q=${encodeURIComponent(q)}`);
    setSearch('');
  }

  return (
    <div className={s.shell}>
      <header ref={headerRef} className={s.header}>
        <div className={s.brand}>
          <img src={logo} alt="" className={s.logo} />
          <div>
            <div className={s.wordmark}>Sterling</div>
            <div className={s.byline}>by cardinale</div>
          </div>
        </div>

        <nav className={s.nav} aria-label="Main">
          {navigationLinks(false)}
        </nav>

        <div className={s.actions}>
          <form className={s.search} onSubmit={submitSearch} role="search">
            <SearchIcon />
            <input
              className={s.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search transactions"
            />
          </form>

          <PrivacyToggle className={s.privacy} />

          <Button
            variant="primary"
            className={s.record}
            aria-label="Record a transaction"
            onClick={() => setRecordOpen(true)}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            <span className={s.recordLabel}>Record</span>
          </Button>

          {/*
            The menu lives INSIDE the ref'd wrapper deliberately. When it sat
            outside, the outside-click handler saw mousedown on a menu item as
            a click elsewhere, closed the menu, and unmounted the button before
            its click event could fire — so neither item did anything.
          */}
          <div ref={menuRef} className={s.menuWrap}>
            <button
              type="button"
              className={s.avatar}
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Account menu"
            >
              {user ? initials(user.name) : '··'}
            </button>

            {menuOpen && user ? (
              <div className={s.menu} role="menu">
                <div className={s.menuHead}>
                  <div className={s.menuName}>{user.name}</div>
                  <div className={s.menuEmail}>{user.email}</div>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className={s.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                >
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className={s.main}>
        <VerifyEmailBanner />
        {/*
          Keyed on the mask so flipping private mode remounts the current view.
          The formatters are plain functions reading a module flag — nothing
          below subscribes to it — so without this the figures would only
          change on the next unrelated render.
        */}
        <Outlet
          key={masked ? 'masked' : 'shown'}
          context={{ openRecord: () => setRecordOpen(true), toast }}
        />
      </main>

      <div className={s.bottomBar}>
        <nav
          ref={bottomNavRef}
          className={s.bottomNav}
          aria-label="Main"
          onScroll={updateScrollEdges}
        >
          {navigationLinks(true)}
        </nav>
        <span
          className={cx(s.scrollHint, s.scrollHintLeft, scrollEdges.left && s.scrollHintVisible)}
          aria-hidden="true"
        >
          ‹
        </span>
        <span
          className={cx(s.scrollHint, s.scrollHintRight, scrollEdges.right && s.scrollHintVisible)}
          aria-hidden="true"
        >
          ›
        </span>
      </div>

      {recordOpen ? (
        <RecordModal
          onClose={() => setRecordOpen(false)}
          onSaved={(message) => {
            setRecordOpen(false);
            toast.show(message);
          }}
        />
      ) : null}

      <PrivacyPrompt />

      <ToastHost toast={toast} />
    </div>
  );
}

/** Page title block, used by each view so the header row stays consistent. */
export function PageHead({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={s.pageHead}>
      <div>
        <h1 className={s.pageTitle}>{title}</h1>
        {subtitle ? <div className={s.pageSub}>{subtitle}</div> : null}
      </div>
      {action ? <div className={s.rangeSwitch}>{action}</div> : null}
    </div>
  );
}
